import React, { useMemo, useRef, useState } from 'react';

import { FormControl, Menu, MenuItem, Select } from '@material-ui/core';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';

import { useStyles } from '../../hooks/useStyles';
import { Event } from '../../types';

type Props = {
  events: Event[];
  selectedEventId: number;
  onSelect: (eventId: number) => void;
};

export const IdeaEventSelector = ({
  events,
  selectedEventId,
  onSelect,
}: Props) => {
  const classes = useStyles();
  const [mainAnchor, setMainAnchor] = useState<null | HTMLElement>(null);
  const [subAnchor, setSubAnchor] = useState<null | HTMLElement>(null);
  const [activeYear, setActiveYear] = useState<string | null>(null);
  const yearItemRefs = useRef<Record<string, HTMLElement | null>>({});

  const eventsByYear = useMemo(
    () =>
      events.reduce<Record<string, Event[]>>((acc, event) => {
        if (event.id === undefined)
          throw new Error(`Event "${event.title}" is missing an id`);
        const year = new Date(event.startDate).getFullYear().toString();
        if (!acc[year]) acc[year] = [];
        acc[year].push(event);
        return acc;
      }, {}),
    [events],
  );

  const years = useMemo(
    () => Object.keys(eventsByYear).sort((a, b) => Number(b) - Number(a)),
    [eventsByYear],
  );

  const handleMainClose = () => {
    setMainAnchor(null);
    setSubAnchor(null);
    setActiveYear(null);
  };

  const handleSubClose = () => {
    setSubAnchor(null);
    setActiveYear(null);
  };

  const handleYearClick = (year: string) => {
    setActiveYear(year);
    setSubAnchor(yearItemRefs.current[year]);
  };

  const handleEventSelect = (eventId: number) => {
    onSelect(eventId);
    handleMainClose();
  };

  return (
    <>
      <FormControl className={classes.formControl}>
        <Select
          className={classes.select}
          value={selectedEventId}
          open={false}
          onOpen={e => setMainAnchor(e.currentTarget as HTMLElement)}
        >
          <MenuItem value={0}>All events</MenuItem>
          {events.map(event => (
            <MenuItem key={event.id} value={event.id}>
              {event.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Menu
        anchorEl={mainAnchor}
        open={Boolean(mainAnchor)}
        onClose={handleMainClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={() => handleEventSelect(0)}>All events</MenuItem>
        {years.map(year => (
          <MenuItem
            key={year}
            ref={el => {
              yearItemRefs.current[year] = el;
            }}
            selected={activeYear === year}
            onClick={() => handleYearClick(year)}
          >
            {year}
            <ArrowRightIcon style={{ marginLeft: 'auto' }} />
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={subAnchor}
        open={Boolean(subAnchor)}
        onClose={handleSubClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {activeYear &&
          eventsByYear[activeYear]?.map(event => (
            <MenuItem
              key={event.id}
              selected={event.id === selectedEventId}
              onClick={() => handleEventSelect(event.id!)}
            >
              {event.title}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};
